/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, February 25th 2022, 8:35:25 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import {
  useAPIFetchItem,
  useAPIMutation,
  useAPIRequest,
  useAPIFetchList,
  UseFetchListArgs,
  APISERVICEPATHS,
  useAPIForm,
  UseAPIFormSpec,
} from "lib/api";
import { FormConfiguration, FieldDependency, FieldDependencyValidationSchema } from "lib/schemas";
import { PrimaryKeyValue } from "lib/types";
import { useDeleteOSItem } from "version-control/services";

const formsAPIServicePath = APISERVICEPATHS.forms;

type FormsGETAPIHookSpec = Omit<UseFetchListArgs<FormConfiguration>, "apiServicePath">;

const useAPIFetchForm = (
  formId: PrimaryKeyValue<FormConfiguration>,
  fetchOptions: Omit<FormsGETAPIHookSpec, "apiPathParams">
) =>
  useAPIFetchItem<FormConfiguration>({
    apiServicePath: formsAPIServicePath,
    apiPathParams: [formId],
    ...fetchOptions,
  });

const useAPIListForms = (args: FormsGETAPIHookSpec) =>
  useAPIFetchList<FormConfiguration>({
    apiServicePath: formsAPIServicePath,
    ...args,
  });

const useAPIMutateForm = () =>
  useAPIMutation<FormConfiguration>({
    apiServicePath: formsAPIServicePath,
  });

const fieldDependenciesAPIServicePath = APISERVICEPATHS.fieldDependencies;

interface UseFieldDependenciesAPIFormSpec {
  fieldDependency: FieldDependency;
  updateFieldDependencyFromAPICallback: UseAPIFormSpec<FieldDependency>["setAPIFormResponse"];
}

const useFieldDependenciesAPIForm = ({
  fieldDependency,
  updateFieldDependencyFromAPICallback,
}: UseFieldDependenciesAPIFormSpec) => {
  const useAPIFormReturn = useAPIForm({
    apiServicePath: fieldDependenciesAPIServicePath,
    apiFormDataValidationSchema: FieldDependencyValidationSchema,
    apiFormDefaultValues: fieldDependency,
    apiInitialFetchId: null,
    primaryKey: "id",
    setAPIFormResponse: updateFieldDependencyFromAPICallback,
    formResetDependencyArray: [fieldDependency?.id],
  });

  return useAPIFormReturn;
};

export const useFormsAPI = () => ({ useAPIFetchForm, useAPIListForms, useAPIMutateForm });

export const useFieldDependenciesAPI = () => ({
  useList: () =>
    useAPIRequest<FieldDependency[]>({
      apiServicePath: fieldDependenciesAPIServicePath,
      makeRequestOnInitialMount: false,
    }),
  useFetch: () =>
    useAPIRequest<FieldDependency>({
      apiServicePath: fieldDependenciesAPIServicePath,
      makeRequestOnInitialMount: false,
    }),
  useMutate: () =>
    useAPIMutation<FieldDependency>({
      apiServicePath: fieldDependenciesAPIServicePath,
    }),
  useFieldDependenciesAPIForm,
  useDelete: () => useDeleteOSItem(fieldDependenciesAPIServicePath),
});
